import { dayjs } from '../support/helpers/dayjs.ts';
import { IAuthorityErrata, CachedErrataProvider } from '@autopylot-internal/tiles-client';
import { State } from '@autopylot-internal/state';
import { Feature } from 'geojson';
import { IAuthorizableGeometry, IAuthorizableProperties } from '@autopylot-internal/tiles-client';
import { Logger } from '../support/debug/Logger.ts';
import { BufferedEventBus } from '../events/BufferedEventBus.ts';

// Check for errata changes every minute. This does not make network requests —
// it reads from CachedErrataProvider's cache. CachedErrataProvider handles the
// actual HTTP polling independently.
const POLL_EVERY_MS = 1 * 60 * 1000; // 1 min

// how long to allow errata to fail before logging error to sentry
const LOG_ERROR_AFTER_MS = 10 * 60 * 1000; // 10 mins

export class ErrataState extends State<{}> {
    static inject = () => [CachedErrataProvider, Logger, BufferedEventBus];
    constructor(private errataProvider: CachedErrataProvider, private log: Logger, private events: BufferedEventBus){
        super({});
        this.events.on('AppForegrounded', this.onForegrounded);
        this.events.on('AppBackgrounded', this.onBackgrounded);
    }

    private currentErrata: IAuthorityErrata | undefined;

    private isForegrounded = true;
    // start of the current window during which errata could have been fetched but hasn't been.
    // Reset whenever we (re)enter the foreground or detect the app was suspended, so the watchdog
    // never counts time the app spent backgrounded / asleep (when fetching is intentionally paused).
    private errataTimestamp = Date.now();
    private logTimestamp = Date.now(); // when we last logged an error about failed errata
    private lastPollAt = Date.now(); // when syncLocalErrataFromRemote last ran

    private resetFailureWindow = () => {
        const now = Date.now();
        this.errataTimestamp = now;
        this.logTimestamp = now;
    };

    private onForegrounded = async () => {
        this.isForegrounded = true;
        // we paused fetching while backgrounded, so don't count that gap, and fetch again promptly
        this.resetFailureWindow();
        await this.errataProvider.enablePolling();
    };

    private onBackgrounded = () => {
        this.isForegrounded = false;
        this.errataProvider.disablePolling();
    };

    private syncLocalErrataFromRemote = async () => {
        const now = Date.now();
        const sinceLastPoll = now - this.lastPollAt;
        this.lastPollAt = now;

        // If far more than the poll interval elapsed, the app's timers were frozen (backgrounded
        // or the device was asleep), so the gap is not a real fetch outage. Restart the window.
        // This also covers suspends that produce no foreground/background event.
        if (sinceLastPoll > POLL_EVERY_MS * 2) {
            this.resetFailureWindow();
        }

        if (!this.isForegrounded) {
            this.errataProvider.disablePolling();
            return;
        }

        await this.errataProvider.enablePolling();

        const errata = await this.errataProvider.getErrata();

        // Only ever advance the window forward. A lastSuccessAt from before a suspend must not drag
        // it back past a reset, or we'd re-report the backgrounded gap as an outage.
        const lastSuccessAt = this.errataProvider.getLastSuccessAt();
        if (lastSuccessAt && lastSuccessAt > this.errataTimestamp) {
            this.errataTimestamp = lastSuccessAt;
        }

        if (now - this.errataTimestamp > LOG_ERROR_AFTER_MS) {
            if (now - this.logTimestamp > LOG_ERROR_AFTER_MS) {
                const errataAge = dayjs().diff(dayjs(this.errataTimestamp), 'm');
                const lastError = this.errataProvider.getLastError();
                const wrappedError = new Error(`errata has not been successfully fetched for ${errataAge} mins${lastError ? `. Inner Error: ${String(lastError)}` : ''}`);
                // copy over the inner error code so Sentry's fetch.failed filter still applies
                (wrappedError as any).errorCode = (lastError as any)?.errorCode;
                this.log.error(wrappedError);
                this.logTimestamp = now;
            }
        }

        if (!this.currentErrata || JSON.stringify(this.currentErrata) !== JSON.stringify(errata)){
            this.currentErrata = errata;
            this.setState({});
        }
    };

    pollForErrata = async () => {
        await this.errataProvider.enablePolling();
        while (true){
            try {
                await this.syncLocalErrataFromRemote();
            }
            catch (err) {
                // never let an unexpected error kill the polling loop, or the watchdog stops forever
                this.log.error(err as Error);
            }
            await new Promise(resolve => setTimeout(resolve, POLL_EVERY_MS));
        }
    };

    getRemoveIds = (): string[] => {
        if (!this.currentErrata){
            return [];
        }
        return this.currentErrata.removes.map(r => r.GLOBAL_ID).concat(this.currentErrata.changes.map(f => f.properties!.GLOBAL_ID));
    };

    getErrataFeatures = (): Feature<IAuthorizableGeometry, IAuthorizableProperties>[] => {
        if (!this.currentErrata){
            return [];
        }
        return this.currentErrata.adds.concat(this.currentErrata.changes);
    };
}
